'use client';
import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Footer from '@/components/common/Footer';
import { createClient } from '@/lib/supabase/client';

interface FormData {
  businessName: string;
  websiteUrl: string;
  industry: string;
  contactName: string;
  email: string;
  phone: string;
  biggestComplaint: string;
  consentGiven: boolean;
}

interface FormErrors {
  businessName?: string;
  websiteUrl?: string;
  industry?: string;
  contactName?: string;
  email?: string;
  phone?: string;
  biggestComplaint?: string;
  consentGiven?: string;
}

const industries = [
  'Auto Detailing',
  'Restaurant / Food & Beverage',
  'Salon / Barber',
  'Med Spa / Aesthetics',
  'Contractor / Construction',
  'Local Service Business',
  'Real Estate / Brokerage',
  'Retail / E-commerce',
  'Health & Wellness',
  'Professional Services',
  'Other',
];

const faqItems = [
  {
    id: 'faq-1',
    question: 'Is there a price listed on this page?',
    answer:
      'No. This page is about getting selected for a concept build first. We care more about fit than forcing a pricing conversation before you have seen anything.',
  },
  {
    id: 'faq-2',
    question: 'What happens after I submit?',
    answer:
      'We review your business and your current site. If it feels like a strong fit, we may build a concept for you and send it over for review.',
  },
  {
    id: 'faq-3',
    question: 'Are you really building concepts before I commit?',
    answer:
      'Yes, for select businesses. That is the whole point of Website Rescue.',
  },
  {
    id: 'faq-4',
    question: 'Why are you doing this?',
    answer:
      'Because the strongest way to show what we can do is to actually do it. This also helps us build a portfolio of transformation work.',
  },
  {
    id: 'faq-5',
    question: 'Can you use my business in your portfolio even if I do not buy?',
    answer:
      'Yes, that is part of the Website Rescue model. You agree to that when submitting your site.',
  },
  {
    id: 'faq-6',
    question: 'What happens if I like the concept?',
    answer:
      'If you like it, we can discuss the next step to secure it and move the project forward.',
  },
  {
    id: 'faq-7',
    question: 'What if I do not want it?',
    answer: 'No pressure. You are not obligated to move forward.',
  },
  {
    id: 'faq-8',
    question: 'Do you also launch the site?',
    answer:
      'Yes, launch, handoff, domain connection, and ongoing support can be handled separately.',
  },
];

const businessTypes = [
  { label: 'Auto Detailers', icon: '🚗' },
  { label: 'Restaurants', icon: '🍽️' },
  { label: 'Salons & Barbers', icon: '✂️' },
  { label: 'Med Spas', icon: '💆' },
  { label: 'Contractors', icon: '🔨' },
  { label: 'Local Service Businesses', icon: '🏪' },
  { label: 'Brokerages', icon: '🏢' },
  { label: 'Small Premium Brands', icon: '⭐' },
];

const improvements = [
  {
    title: 'Cleaner Visual Identity',
    desc: 'A sharper, more professional look that reflects the quality of your business.',
  },
  {
    title: 'Better Mobile Experience',
    desc: 'Layouts that work on every screen — especially the phones your customers use.',
  },
  {
    title: 'Stronger Calls to Action',
    desc: 'Clear next steps that guide visitors toward contacting or booking you.',
  },
  {
    title: 'Improved Trust & Credibility',
    desc: 'Design signals that make visitors feel confident choosing your business.',
  },
  {
    title: 'Clearer Services & Offers',
    desc: 'Messaging that explains what you do and why it matters — without confusion.',
  },
  {
    title: 'Better Brand Perception',
    desc: 'An overall presence that matches the caliber of the work you actually deliver.',
  },
];

const transformations = [
  {
    before: 'Outdated homepage with no clear hierarchy, clashing fonts, and a broken mobile layout.',
    after: 'Premium redesign with strong visual hierarchy, clean typography, and full mobile optimization.',
    category: 'Homepage Transformation',
  },
  {
    before: 'Weak service page with no pricing context, buried CTAs, and generic stock photography.',
    after: 'Conversion-focused service page with clear offer framing, prominent CTAs, and on-brand visuals.',
    category: 'Service Page Redesign',
  },
  {
    before: 'Confusing site structure with too many navigation items, no clear user flow, and slow load times.',
    after: 'Streamlined mobile-first layout with focused navigation, fast performance, and a clear path to contact.',
    category: 'Site Structure Overhaul',
  },
];

export default function WebsiteRescuePage() {
  const [formData, setFormData] = useState<FormData>({
    businessName: '',
    websiteUrl: '',
    industry: '',
    contactName: '',
    email: '',
    phone: '',
    biggestComplaint: '',
    consentGiven: false,
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.08 }
    );
    reveals?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    if (!formData.businessName.trim()) errors.businessName = 'Business name is required.';
    if (!formData.websiteUrl.trim()) errors.websiteUrl = 'Website URL is required.';
    if (!formData.industry) errors.industry = 'Please select your industry.';
    if (!formData.contactName.trim()) errors.contactName = 'Your name is required.';
    if (!formData.email.trim()) errors.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Please enter a valid email.';
    if (!formData.consentGiven) errors.consentGiven = 'You must agree to the terms to submit.';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const supabase = createClient();
      const { error } = await supabase.from('website_rescue_submissions').insert({
        business_name: formData.businessName.trim(),
        website_url: formData.websiteUrl.trim(),
        industry: formData.industry,
        contact_name: formData.contactName.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim() || null,
        biggest_complaint: formData.biggestComplaint.trim() || null,
        consent_given: formData.consentGiven,
      });
      if (error) throw error;

      // Tier 1: fire lead notification + auto-response email
      fetch('/api/submit-website-rescue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          formData: {
            contact_name: formData.contactName.trim(),
            email: formData.email.trim().toLowerCase(),
            phone: formData.phone.trim() || null,
            business_name: formData.businessName.trim(),
            website_url: formData.websiteUrl.trim(),
            industry: formData.industry,
            biggest_complaint: formData.biggestComplaint.trim() || null,
          },
        }),
      }).catch(() => {});

      setSubmitSuccess(true);
    } catch (err: any) {
      setSubmitError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (formErrors[name as keyof FormErrors]) {
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  return (
    <>
      {/* Minimal Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-md border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link href="/" className="flex-shrink-0">
            <AppImage
              src="/assets/images/boc_logo_240x90_white-1771394304209.png"
              alt="Best Odds Corp Logo"
              width={150}
              height={56}
              priority
              className="w-[110px] sm:w-[140px] h-auto"
            />
          </Link>
          <button
            onClick={scrollToForm}
            className="px-4 sm:px-5 py-2 bg-primary text-primary-foreground text-[10px] uppercase tracking-widest font-semibold hover:bg-primary/90 transition-colors whitespace-nowrap"
          >
            Submit Your Website
          </button>
        </div>
      </nav>

      <main className="min-h-screen bg-background text-foreground pt-[60px] sm:pt-[68px]">

        {/* ── HERO ── */}
        <section
          className="relative py-24 sm:py-32 md:py-40 px-4 sm:px-6 border-b border-border overflow-hidden"
          style={{
            background: `
              radial-gradient(ellipse 80% 60% at 50% 0%, rgba(229,9,20,0.10) 0%, transparent 65%),
              radial-gradient(ellipse 100% 80% at 50% 100%, rgba(0,0,0,0.6) 0%, transparent 70%),
              #0A0A0A
            `,
          }}
        >
          <div className="max-w-[900px] mx-auto text-center relative z-10">
            <div className="reveal">
              <span className="inline-block text-[10px] uppercase tracking-ultra-wide text-primary font-medium mb-6 sm:mb-8">
                Website Rescue — Best Odds Corp
              </span>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.0] mb-6 sm:mb-8">
                Your website looks outdated.
                <br />
                <span className="text-primary">We'll rebuild it first.</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-6 leading-relaxed">
                We create a redesigned website concept for select businesses with outdated sites — so you can see the vision before deciding whether to move forward.
              </p>
              <p className="text-sm text-muted-foreground/70 mb-10 sm:mb-12">
                Built for local businesses that need a stronger first impression online.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                <button
                  onClick={scrollToForm}
                  className="w-full sm:w-auto px-8 py-4 bg-primary text-primary-foreground text-[11px] uppercase tracking-widest font-semibold hover:bg-primary/90 transition-colors border-glow-strong"
                >
                  Submit Your Website
                </button>
                <a
                  href="#how-it-works"
                  className="w-full sm:w-auto px-8 py-4 border border-border text-foreground text-[11px] uppercase tracking-widest font-semibold hover:border-border-hover hover:bg-white/5 transition-colors text-center"
                >
                  See How It Works
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── WHY OUTDATED WEBSITES LOSE BUSINESS ── */}
        <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1100px] mx-auto">
            <div className="reveal text-center mb-12">
              <span className="text-[10px] uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                The Cost of Doing Nothing
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-h2 font-bold tracking-tight mb-4">
                Why outdated websites lose business
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {[
                { stat: '75%', desc: 'of users judge a business\'s credibility based on its website design.' },
                { stat: '57%', desc: 'of internet users say they won\'t recommend a business with a poor mobile site.' },
                { stat: '8 sec', desc: 'is the average time a visitor decides whether to stay or leave your site.' },
              ].map((item, i) => (
                <div key={i} className="reveal p-6 sm:p-8 border border-border bg-background">
                  <p className="text-4xl sm:text-5xl font-bold text-primary mb-3">{item.stat}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section id="how-it-works" className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 border-b border-border">
          <div className="max-w-[1100px] mx-auto">
            <div className="reveal text-center mb-12 sm:mb-16">
              <span className="text-[10px] uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                The Process
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-h2 font-bold tracking-tight mb-4">
                How Website Rescue works
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
                No public pricing, no long proposal process, no pressure. We show you something real first.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {[
                { step: '01', title: 'You send us your current website', desc: 'Submit your site URL and a few details about your business through the form below.' },
                { step: '02', title: 'We review it and decide if it is a good fit', desc: 'We evaluate your site against our criteria. Not every submission moves forward — we are selective by design.' },
                { step: '03', title: 'We build a redesigned concept', desc: 'If selected, we create a full concept redesign using your brand materials and our design system.' },
                { step: '04', title: 'You review it and decide', desc: 'We share the concept with you. If you like it, we discuss next steps. If not, no pressure and no obligation.' },
              ].map((item) => (
                <div key={item.step} className="reveal p-6 sm:p-8 border border-border bg-surface-1 relative">
                  <span className="text-[10px] uppercase tracking-ultra-wide text-primary font-semibold mb-4 block">
                    Step {item.step}
                  </span>
                  <h3 className="text-base sm:text-lg font-semibold mb-3 leading-snug">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── WHY THIS OFFER EXISTS ── */}
        <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 border-b border-border bg-surface-1">
          <div className="max-w-[900px] mx-auto">
            <div className="reveal">
              <span className="text-[10px] uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                The Reason
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-h2 font-bold tracking-tight mb-6 sm:mb-8">
                Why we are doing this
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-6">
                Best Odds Corp is building a stronger portfolio of website transformations for small businesses. Website Rescue is how we do that. We identify outdated websites with clear upside, redesign them, and use the resulting concepts as proof of what we can do.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8 sm:mt-10">
                <div className="p-6 border border-primary bg-background">
                  <p className="text-[10px] uppercase tracking-ultra-wide text-primary font-semibold mb-3">For Your Business</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    You get to see a stronger, more professional version of your website — built to your brand — before committing to anything.
                  </p>
                </div>
                <div className="p-6 border border-border bg-background">
                  <p className="text-[10px] uppercase tracking-ultra-wide text-muted-foreground font-semibold mb-3">For Best Odds Corp</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    We get to showcase real transformation work and build a portfolio that demonstrates exactly what we are capable of.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── WHO THIS IS FOR ── */}
        <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 border-b border-border">
          <div className="max-w-[1100px] mx-auto">
            <div className="reveal text-center mb-12">
              <span className="text-[10px] uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                Ideal Fit
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-h2 font-bold tracking-tight mb-4">
                Best fit for businesses like these
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
                Especially strong fit for businesses with weak design, poor mobile layout, unclear messaging, or outdated branding.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {businessTypes.map((type) => (
                <div
                  key={type.label}
                  className="reveal p-5 sm:p-6 border border-border bg-surface-1 text-center card-hover"
                >
                  <span className="text-2xl sm:text-3xl block mb-3">{type.icon}</span>
                  <p className="text-xs sm:text-sm font-medium leading-snug">{type.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── WHAT WE IMPROVE ── */}
        <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1100px] mx-auto">
            <div className="reveal text-center mb-12">
              <span className="text-[10px] uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                The Upgrade
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-h2 font-bold tracking-tight mb-4">
                What usually gets better
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {improvements.map((item) => (
                <div key={item.title} className="reveal p-6 sm:p-8 border border-border bg-background">
                  <div className="w-6 h-0.5 bg-primary mb-4" />
                  <h3 className="text-base sm:text-lg font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── TRANSFORMATION TEASER ── */}
        <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 border-b border-border">
          <div className="max-w-[1100px] mx-auto">
            <div className="reveal text-center mb-12">
              <span className="text-[10px] uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                Concept Work
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-h2 font-bold tracking-tight mb-4">
                The kind of transformation we are looking for
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
                Illustrative examples of the type of work Website Rescue produces. Real case studies will be added as concepts are completed.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
              {transformations.map((item) => (
                <div key={item.category} className="reveal border border-border bg-surface-1 overflow-hidden">
                  {/* Before */}
                  <div className="p-5 sm:p-6 border-b border-border">
                    <span className="text-[9px] uppercase tracking-ultra-wide text-muted-foreground font-semibold mb-3 block">Before</span>
                    <div className="w-full h-28 sm:h-32 bg-surface-3 border border-border/50 flex items-center justify-center mb-4">
                      <span className="text-[10px] uppercase tracking-widest text-muted-foreground/50">Outdated Site</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{item.before}</p>
                  </div>
                  {/* After */}
                  <div className="p-5 sm:p-6">
                    <span className="text-[9px] uppercase tracking-ultra-wide text-primary font-semibold mb-3 block">After</span>
                    <div
                      className="w-full h-28 sm:h-32 border border-primary/30 flex items-center justify-center mb-4"
                      style={{ background: 'radial-gradient(ellipse at center, rgba(229,9,20,0.08) 0%, transparent 70%), #141414' }}
                    >
                      <span className="text-[10px] uppercase tracking-widest text-primary/60">Concept Redesign</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{item.after}</p>
                  </div>
                  <div className="px-5 sm:px-6 pb-5 sm:pb-6">
                    <span className="text-[9px] uppercase tracking-ultra-wide text-muted-foreground/60 font-medium">{item.category}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CREDIBILITY ── */}
        <section className="py-12 sm:py-16 px-4 sm:px-6 border-b border-border bg-surface-1">
          <div className="max-w-[900px] mx-auto">
            <div className="reveal flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-10">
              <div className="flex-shrink-0">
                <AppImage
                  src="/assets/images/boc_logo_240x90_white-1771394304209.png"
                  alt="Best Odds Corp"
                  width={140}
                  height={52}
                  className="opacity-80 w-[110px] sm:w-[140px] h-auto"
                />
              </div>
              <div>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Best Odds Corp is a growth and execution agency working with small businesses, operators, and founders. We build websites, run paid media, and handle the systems that drive growth. Website Rescue is our portfolio-building initiative — and your opportunity to see what a stronger online presence could look like.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── SUBMISSION FORM ── */}
        <section ref={formRef} id="submit" className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 border-b border-border">
          <div className="max-w-[700px] mx-auto">
            <div className="reveal text-center mb-10 sm:mb-12">
              <span className="text-[10px] uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                Apply Now
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-h2 font-bold tracking-tight mb-4">
                Submit your website
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground max-w-lg mx-auto">
                Send us your current site and we'll review whether it is a fit for Website Rescue.
              </p>
            </div>

            {submitSuccess ? (
              <div className="reveal p-8 sm:p-10 border border-primary bg-surface-1 text-center">
                <div className="w-8 h-0.5 bg-primary mx-auto mb-6" />
                <h3 className="text-xl sm:text-2xl font-bold mb-3">Submission received.</h3>
                <p className="text-sm text-muted-foreground leading-relaxed max-w-md mx-auto">
                  We will review your site and reach out if it is a strong fit for Website Rescue. This is a selective process — we appreciate your interest.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="reveal space-y-5">
                {/* Business Name */}
                <div>
                  <label className="block text-[10px] uppercase tracking-widest font-medium mb-2">
                    Business Name <span className="text-primary">*</span>
                  </label>
                  <input
                    type="text"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleChange}
                    placeholder="Your business name"
                    className={`w-full px-4 py-3 bg-surface-1 border text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary transition-colors ${formErrors.businessName ? 'border-error' : 'border-border'}`}
                  />
                  {formErrors.businessName && (
                    <p className="text-xs text-error mt-1.5">{formErrors.businessName}</p>
                  )}
                </div>

                {/* Website URL */}
                <div>
                  <label className="block text-[10px] uppercase tracking-widest font-medium mb-2">
                    Website URL <span className="text-primary">*</span>
                  </label>
                  <input
                    type="url"
                    name="websiteUrl"
                    value={formData.websiteUrl}
                    onChange={handleChange}
                    placeholder="https://yourbusiness.com"
                    className={`w-full px-4 py-3 bg-surface-1 border text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary transition-colors ${formErrors.websiteUrl ? 'border-error' : 'border-border'}`}
                  />
                  {formErrors.websiteUrl && (
                    <p className="text-xs text-error mt-1.5">{formErrors.websiteUrl}</p>
                  )}
                </div>

                {/* Industry */}
                <div>
                  <label className="block text-[10px] uppercase tracking-widest font-medium mb-2">
                    Industry <span className="text-primary">*</span>
                  </label>
                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 bg-surface-1 border text-sm text-foreground focus:outline-none focus:border-primary transition-colors appearance-none ${formErrors.industry ? 'border-error' : 'border-border'} ${!formData.industry ? 'text-muted-foreground/50' : ''}`}
                  >
                    <option value="" disabled>Select your industry</option>
                    {industries.map((ind) => (
                      <option key={ind} value={ind}>{ind}</option>
                    ))}
                  </select>
                  {formErrors.industry && (
                    <p className="text-xs text-error mt-1.5">{formErrors.industry}</p>
                  )}
                </div>

                {/* Name + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest font-medium mb-2">
                      Your Name <span className="text-primary">*</span>
                    </label>
                    <input
                      type="text"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleChange}
                      placeholder="First and last name"
                      className={`w-full px-4 py-3 bg-surface-1 border text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary transition-colors ${formErrors.contactName ? 'border-error' : 'border-border'}`}
                    />
                    {formErrors.contactName && (
                      <p className="text-xs text-error mt-1.5">{formErrors.contactName}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest font-medium mb-2">
                      Email <span className="text-primary">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@yourbusiness.com"
                      className={`w-full px-4 py-3 bg-surface-1 border text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary transition-colors ${formErrors.email ? 'border-error' : 'border-border'}`}
                    />
                    {formErrors.email && (
                      <p className="text-xs text-error mt-1.5">{formErrors.email}</p>
                    )}
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-[10px] uppercase tracking-widest font-medium mb-2">
                    Phone <span className="text-muted-foreground/50">(Optional)</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 000-0000"
                    className="w-full px-4 py-3 bg-surface-1 border border-border text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary transition-colors"
                  />
                </div>

                {/* Biggest Complaint */}
                <div>
                  <label className="block text-[10px] uppercase tracking-widest font-medium mb-2">
                    What do you dislike most about your current website?
                  </label>
                  <textarea
                    name="biggestComplaint"
                    value={formData.biggestComplaint}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Tell us what is not working — design, mobile, messaging, conversions, etc."
                    className="w-full px-4 py-3 bg-surface-1 border border-border text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary transition-colors resize-none"
                  />
                </div>

                {/* Consent Checkbox */}
                <div className={`p-5 border ${formErrors.consentGiven ? 'border-error' : 'border-border'} bg-surface-1`}>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="consentGiven"
                      checked={formData.consentGiven}
                      onChange={handleChange}
                      className="mt-0.5 flex-shrink-0 w-4 h-4 border border-border bg-surface-2 accent-primary cursor-pointer"
                    />
                    <span className="text-xs text-muted-foreground leading-relaxed">
                      I authorize Best Odds Corp to create concept work using my business's publicly available brand materials and understand that resulting concepts may be displayed in Best Odds Corp's portfolio, case studies, social media, ads, and promotional materials, whether or not I purchase the concept.{' '}
                      <button
                        type="button"
                        onClick={() => setTermsModalOpen(true)}
                        className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors"
                      >
                        View terms
                      </button>
                    </span>
                  </label>
                  {formErrors.consentGiven && (
                    <p className="text-xs text-error mt-2 pl-7">{formErrors.consentGiven}</p>
                  )}
                </div>

                {submitError && (
                  <p className="text-sm text-error border border-error/30 bg-error/5 px-4 py-3">
                    {submitError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-primary text-primary-foreground text-[11px] uppercase tracking-widest font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed border-glow"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit for Review'}
                </button>

                <p className="text-xs text-muted-foreground/60 text-center">
                  Submission does not guarantee selection. We review every site and reach out only to strong fits.
                </p>
              </form>
            )}
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 border-b border-border bg-surface-1">
          <div className="max-w-[800px] mx-auto">
            <div className="reveal text-center mb-10 sm:mb-12">
              <span className="text-[10px] uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                Questions
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-h2 font-bold tracking-tight">FAQ</h2>
            </div>
            <div className="reveal space-y-0 border-t border-border">
              {faqItems.map((item) => (
                <div key={item.id} className="border-b border-border">
                  <button
                    onClick={() => setOpenFaq(openFaq === item.id ? null : item.id)}
                    className="w-full flex items-start justify-between gap-4 py-5 sm:py-6 text-left"
                  >
                    <span className="text-sm sm:text-base font-medium leading-snug pr-2">{item.question}</span>
                    <span className="flex-shrink-0 mt-0.5">
                      <svg
                        className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${openFaq === item.id ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>
                  </button>
                  {openFaq === item.id && (
                    <div className="pb-5 sm:pb-6">
                      <p className="text-sm text-muted-foreground leading-relaxed">{item.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FINAL CTA ── */}
        <section
          className="py-20 sm:py-28 md:py-32 px-4 sm:px-6"
          style={{
            background: `
              radial-gradient(ellipse 70% 50% at 50% 100%, rgba(229,9,20,0.09) 0%, transparent 65%),
              #0A0A0A
            `,
          }}
        >
          <div className="max-w-[800px] mx-auto text-center">
            <div className="reveal">
              <span className="text-[10px] uppercase tracking-ultra-wide text-primary font-medium mb-6 block">
                Website Rescue
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-5 sm:mb-6 leading-tight">
                Send us your website.
                <br />
                <span className="text-muted-foreground">We'll tell you if it is worth rescuing.</span>
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mb-8 sm:mb-10 max-w-lg mx-auto">
                We are looking for businesses with real upside, not just anyone with a website.
              </p>
              <button
                onClick={scrollToForm}
                className="px-10 py-4 bg-primary text-primary-foreground text-[11px] uppercase tracking-widest font-semibold hover:bg-primary/90 transition-colors border-glow-strong"
              >
                Request Website Rescue
              </button>
              <p className="text-xs text-muted-foreground/50 mt-5">
                Selective by design, built to produce stronger portfolio-quality work.
              </p>
            </div>
          </div>
        </section>

      </main>

      <Footer />

      {/* ── TERMS MODAL ── */}
      {termsModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.85)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setTermsModalOpen(false); }}
        >
          <div className="w-full max-w-[600px] bg-surface-1 border border-border max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-border flex-shrink-0">
              <div>
                <span className="text-[9px] uppercase tracking-ultra-wide text-primary font-semibold block mb-1">
                  Legal
                </span>
                <h3 className="text-lg sm:text-xl font-bold">Concept Preview Terms</h3>
              </div>
              <button
                onClick={() => setTermsModalOpen(false)}
                className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Close modal"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 sm:px-8 py-6 overflow-y-auto flex-1">
              <ul className="space-y-5">
                {[
                  'We may use your business name, logo, website screenshots, social media links, and other publicly visible brand materials to create concept work.',
                  'These concepts are created for review and evaluation unless separately purchased.',
                  'Best Odds Corp may showcase created concepts, including unused concepts, in its portfolio, case studies, website, social posts, ads, decks, and other promotional materials.',
                  'Your existing trademarks, logos, and preexisting brand assets remain yours.',
                  'Best Odds Corp retains ownership of its templates, systems, frameworks, prompts, reusable components, and underlying methods.',
                  'Launch, hosting, domain connection, revisions, and ongoing support are separate unless clearly stated otherwise.',
                  'Final usage rights to any concept are granted only after payment and separate approval.',
                ].map((term, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 border border-border flex items-center justify-center text-[10px] text-muted-foreground font-medium mt-0.5">
                      {i + 1}
                    </span>
                    <p className="text-sm text-muted-foreground leading-relaxed">{term}</p>
                  </li>
                ))}
              </ul>
            </div>

            {/* Modal Footer */}
            <div className="px-6 sm:px-8 py-5 border-t border-border flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <button
                onClick={() => {
                  setFormData((prev) => ({ ...prev, consentGiven: true }));
                  if (formErrors.consentGiven) setFormErrors((prev) => ({ ...prev, consentGiven: undefined }));
                  setTermsModalOpen(false);
                }}
                className="flex-1 py-3 bg-primary text-primary-foreground text-[10px] uppercase tracking-widest font-semibold hover:bg-primary/90 transition-colors"
              >
                I Understand
              </button>
              <button
                onClick={() => setTermsModalOpen(false)}
                className="flex-1 py-3 border border-border text-foreground text-[10px] uppercase tracking-widest font-semibold hover:bg-white/5 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
